import { useCallback } from "react";
import { useLocation } from "react-router-dom";
import AppUpdateNotice from "./AppUpdateNotice";
import { RELOAD_TARGET_STORAGE_KEY, useAppUpdate } from "../hooks/useAppUpdate";
import { hasReloadedForTarget } from "../utils/appVersion";

interface AppUpdateGateProps {
    userLoading: boolean;
    maintenanceReady: boolean;
    maintenanceActive: boolean;
    minVersionReady: boolean;
    belowMinimum: boolean;
    minSupportedVersion: string | null;
}

export default function AppUpdateGate({
    userLoading,
    maintenanceReady,
    maintenanceActive,
    minVersionReady,
    belowMinimum,
    minSupportedVersion,
}: AppUpdateGateProps) {
    const { pathname } = useLocation();
    const {
        ready: appUpdateReady,
        updateAvailable: hostingUpdateAvailable,
        forceUpdate: hostingForceUpdate,
        confirmForceReload,
        dismissUpdate,
    } = useAppUpdate(pathname);

    const reloadTarget = import.meta.env.DEV
        ? null
        : sessionStorage.getItem(RELOAD_TARGET_STORAGE_KEY);

    const minReloadAttempted = Boolean(
        belowMinimum &&
        minSupportedVersion &&
        hasReloadedForTarget(reloadTarget, minSupportedVersion)
    );

    const minForce = Boolean(
        belowMinimum &&
        minSupportedVersion &&
        !minReloadAttempted
    );

    const updateAvailable = belowMinimum || hostingUpdateAvailable;
    // Min reload stores minSupportedVersion, but hosting force checks latestVersion — suppress
    // hosting force after one min reload so a stale bundle does not loop on /.
    const forceUpdate = minForce || (hostingForceUpdate && !minReloadAttempted);

    const onForceReload = useCallback(() => {
        if (belowMinimum && minSupportedVersion) {
            sessionStorage.setItem(RELOAD_TARGET_STORAGE_KEY, minSupportedVersion);
            window.location.reload();
            return;
        }
        confirmForceReload();
    }, [belowMinimum, minSupportedVersion, confirmForceReload]);

    // // For testing: local smoke for AppUpdateNotice (DEV never sets updateAvailable)
    // if (import.meta.env.DEV) {
    //     return (
    //         <AppUpdateNotice
    //             updateAvailable
    //             forceUpdate={false}
    //             onRefresh={() => window.location.reload()}
    //             onForceReload={() => { }}
    //             onDismiss={() => { }}
    //         />
    //     );
    // }

    if (
        userLoading ||
        !maintenanceReady ||
        maintenanceActive ||
        !minVersionReady ||
        !appUpdateReady ||
        !updateAvailable
    ) {
        return null;
    }

    return (
        <AppUpdateNotice
            updateAvailable={updateAvailable}
            forceUpdate={forceUpdate}
            showDismiss={!belowMinimum}
            onRefresh={() => window.location.reload()}
            onForceReload={onForceReload}
            onDismiss={dismissUpdate}
        />
    );
}
